import React, { useState, useEffect, useRef } from 'react';
import BarcodeReader from 'react-barcode-reader';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { WarehouseOrder, ShipmentItem, Warehouse, ShipmentNote } from '../types';
import { normalizeCarrier, getTrackingLink } from '../utils/carrierUtils';
import '../core.css';
import '../OrderDetailsDialog.css';

interface OrderScannerDialogProps {
  order: WarehouseOrder;
  onClose: () => void;
  dialogRef: React.RefObject<HTMLDivElement>;
  refreshOrders: () => Promise<void>;
  warehouseId: number;
}

const OrderScannerDialog: React.FC<OrderScannerDialogProps> = ({ order, onClose, dialogRef, refreshOrders, warehouseId }) => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [scannedItems, setScannedItems] = useState<Set<number>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [shipmentNotes, setShipmentNotes] = useState<ShipmentNote[]>([]);
  const [newNote, setNewNote] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const clickCounts = useRef<Map<number, { count: number; timeout: NodeJS.Timeout | null }>>(new Map());

  useEffect(() => {
    // Only the warehouse name is needed here now that stock is deducted server-side,
    // so this reads /warehouses rather than /neon-stock (which returns the entire
    // product catalogue) — this dialog is opened constantly while packing.
    const fetchNeonStock = async () => {
      try {
        const response = await api.get('/warehouses');
        setWarehouses(response.data.warehouses || []);
      } catch (error: any) {
        console.error('Fetch warehouses error:', error);
        setError(error.response?.data?.error || 'Failed to fetch warehouse data');
        showToast(error.response?.data?.error || 'Failed to fetch warehouse data', 'error');
        setTimeout(() => setError(null), 3000);
      }
    };
    const fetchShipmentNotes = async () => {
      try {
        const response = await api.get(`/shipment-notes?shipment_id=${order.shipments[0].id}&order_id=${order.id}`);
        setShipmentNotes(response.data.notes);
      } catch (err: any) {
        const errorMessage = err.response?.data?.error || 'Failed to load shipment notes';
        setError(errorMessage);
        showToast(errorMessage, 'error');
        setTimeout(() => setError(null), 5000);
      }
    };
    fetchNeonStock();
    fetchShipmentNotes();
    if (inputRef.current) {
      inputRef.current.focus();
    }
    return () => {
      clickCounts.current.forEach(({ timeout }) => {
        if (timeout) clearTimeout(timeout);
      });
    };
  }, [order.id, order.shipments, showToast]);

  const totalQuantity = order.items.length;
  const scannedQuantity = order.items.reduce((sum, item) => {
    if (scannedItems.has(item.id) || item.status === 'Shipped') {
      return sum + 1;
    }
    return sum;
  }, 0);

  const sortedItems = [...order.items].sort((a, b) => {
    if (a.status === b.status) return a.id - b.id;
    return a.status === 'Pending' ? -1 : 1;
  });

  const handleTripleClick = (itemId: number, status: string) => {
    if (status === 'Shipped') return;

    const clickInfo = clickCounts.current.get(itemId) || { count: 0, timeout: null };
    clickInfo.count += 1;

    if (clickInfo.timeout) clearTimeout(clickInfo.timeout);

    clickInfo.timeout = setTimeout(() => {
      if (clickInfo.count >= 3) {
        setScannedItems((prev) => {
          const newSet = new Set([...Array.from(prev)]);
          if (newSet.has(itemId)) {
            newSet.delete(itemId);
          } else {
            newSet.add(itemId);
          }
          return newSet;
        });
      }
      clickInfo.count = 0;
    }, 300);

    clickCounts.current.set(itemId, clickInfo);
  };

  const handleScan = async (data: string) => {
    const gtin = data.trim().slice(-12);
    const pendingItems = order.items
      .filter((item) => item.gtin === gtin && item.status === 'Pending' && !scannedItems.has(item.id))
      .sort((a, b) => a.id - b.id);
    if (pendingItems.length === 0) {
      setError(`GTIN ${gtin} not found or already scanned`);
      showToast(`GTIN ${gtin} not found or already scanned`, 'error');
      setTimeout(() => setError(null), 3000);
      return;
    }

    const item = pendingItems[0];

    try {
      showToast('Processing scan...', 'syncing');
      const itemsToScan = [{ shipment_item_id: item.id }];

      console.log('Scanning item:', {
        order_id: order.id,
        shipment_id: order.shipments[0]?.id,
        shipment_item_id: item.id,
        action: 'Shipped',
        scanned_by: user?.email,
        gtin,
      });

      await api.post('/shipments', {
        action: 'update_scan',
        order_id: order.id,
        shipment_id: order.shipments[0]?.id,
        data: {
          scan_timestamp: new Date().toISOString(),
          scanned_by: user?.email || 'unknown',
          status: 'Shipped',
          items: itemsToScan,
        },
      });

      setScannedItems((prev) => new Set([...Array.from(prev), item.id]));
    } catch (error: any) {
      console.error('Scan error:', error.response?.data || error.message);
      setError(error.response?.data?.error || 'Failed to update scan');
      showToast(error.response?.data?.error || 'Failed to update scan', 'error');
      setTimeout(() => setError(null), 3000);
    }
  };

  const handleError = (err: string) => {
    console.log('Scanner Error:', err);
    setError(err);
    showToast(err, 'error');
    setTimeout(() => setError(null), 3000);
  };

  const handleUnscanAll = async () => {
    try {
      showToast('Unscanning all items...', 'syncing');
      console.log('Unscanning all items for order:', {
        order_id: order.id,
        shipment_id: order.shipments[0]?.id,
      });

      if (!order.shipments[0]?.id) {
        setError('No shipment found to unscan');
        showToast('No shipment found to unscan', 'error');
        setTimeout(() => setError(null), 3000);
        return;
      }

      const scannedItems = order.items.filter((item) => item.status === 'Shipped');
      console.log(`Found ${scannedItems.length} scanned items to unscan`, { items: scannedItems });

      if (scannedItems.length === 0) {
        console.log('No scanned items to unscan for inventory');
      }

      // Stock is credited back server-side by the unscan_all action, in the same
      // transaction as the status reset, and only for items that were actually
      // 'Shipped'. Doing it here meant items with no GTIN never got their stock back.
      await api.post('/shipments', {
        action: 'unscan_all',
        order_id: order.id,
        shipment_id: order.shipments[0].id,
        data: {},
      });

      setScannedItems(new Set());
      await refreshOrders();
      onClose();
    } catch (error: any) {
      console.error('Unscan error:', error.response?.data || error.message);
      setError(error.response?.data?.error || 'Failed to unscan order');
      setTimeout(() => setError(null), 3000);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) {
      showToast('Note cannot be empty', 'error');
      return;
    }
    try {
      showToast('Adding note...', 'syncing');
      const response = await api.post('/shipment-notes', {
        shipment_id: order.shipments[0].id,
        order_id: order.id,
        note: newNote,
        added_by: user?.email || 'unknown',
      });
      setShipmentNotes([...shipmentNotes, response.data]);
      setNewNote('');
      showToast('Note added successfully', 'success');
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || 'Failed to add note';
      setError(errorMessage);
      showToast(errorMessage, 'error');
      setTimeout(() => setError(null), 5000);
    }
  };

  const handleSave = async () => {
    try {
      const scannedIds = Array.from(scannedItems);
      if (scannedIds.length === 0) {
        setError('No items selected to save');
        setTimeout(() => setError(null), 3000);
        return;
      }

      const itemsToScan = order.items
        .filter((item) => scannedIds.includes(item.id) && item.status !== 'Shipped')
        .map((item) => ({
          shipment_item_id: item.id,
        }));

      if (itemsToScan.length === 0) {
        setError('All selected items are already shipped');
        setTimeout(() => setError(null), 3000);
        return;
      }

      console.log('Saving scanned items:', {
        order_id: order.id,
        shipment_id: order.shipments[0]?.id,
        items: itemsToScan,
      });

      const response = await api.post('/shipments', {
        action: 'update_scan',
        order_id: order.id,
        shipment_id: order.shipments[0]?.id,
        data: {
          scan_timestamp: new Date().toISOString(),
          scanned_by: user?.email || 'unknown',
          status: 'Shipped',
          items: itemsToScan,
        },
      });

      // Stock is deducted server-side by the update_scan action above, in the same
      // transaction as the status change. It used to be computed here from a browser
      // snapshot and matched by GTIN, which silently skipped items with no GTIN and
      // could clobber concurrent changes.

      const updatedItemIds = response.data.map((item: ShipmentItem) => item.id);
      setScannedItems((prev) => new Set([...Array.from(prev), ...updatedItemIds]));

      await refreshOrders();
      onClose();
    } catch (error: any) {
      console.error('Save error:', error.response?.data || error.message);
      setError(error.response?.data?.error || 'Failed to save order');
      setTimeout(() => setError(null), 3000);
    }
  };



  const warehouseName = warehouses.find(w => w.id === warehouseId)?.name || `Warehouse ${warehouseId}`;

  const hasScannedItems = scannedItems.size > 0 || order.items.some(item => item.status === 'Shipped');

  return (
    <div className="order-dialog-overlay">
      <div className="order-dialog" ref={dialogRef}>
        <button className="close-button" onClick={onClose}>✕</button>
        <h3 className="order-title">Shipment {order.shipment_reference || 'N/A'} - {warehouseName}</h3>
        <h2 className="customer-name">{order.customer_name || 'N/A'}</h2>
        <div className="quantity-display">
          Total Order Items: {totalQuantity} | Scanned: {scannedQuantity}
        </div>
        <BarcodeReader onScan={handleScan} onError={handleError} />
        {error && <div className="error-message">{error}</div>}
        <div className="scanned-items-table">
          <table>
            <thead>
              <tr>
                <th>Order #</th>
                <th>Shipment Reference</th>
                <th>Tracking Number</th>
                <th>Product Name</th>
                <th>SKU</th>
                <th>GTIN</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {sortedItems.map((item) => (
                <tr
                  key={item.id}
                  className={scannedItems.has(item.id) || item.status === 'Shipped' ? 'shipped-item' : ''}
                  onClick={() => handleTripleClick(item.id, item.status)}
                  style={{ cursor: 'pointer' }}
                  title="Triple-click to toggle scan status"
                >
                  <td>{order.id}</td>
                  <td>{order.shipment_reference || 'N/A'}</td>
                  <td>
                    {order.tracking_number && order.carrier ? (
                      <a
                        href={getTrackingLink(order.tracking_number, order.carrier)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`carrier-chip carrier-chip-${normalizeCarrier(order.carrier).toLowerCase()}`}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {order.tracking_number} ({order.carrier})
                      </a>
                    ) : 'N/A'}
                  </td>
                  <td>{item.product_name}</td>
                  <td>{item.sku || 'N/A'}</td>
                  <td>{item.gtin || 'N/A'}</td>
                  <td>{item.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="two-column-container">
          <div className="left-column">
            <h4>Shipment Notes</h4>
              <textarea
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                placeholder="Enter shipment note..."
                className="note-input"
              />
              <button
                className="action-button"
                onClick={handleAddNote}
                disabled={!newNote.trim()}
              >
                Save Note
              </button>
            <h4>Shipping Label</h4>
            <button
              className="action-button"
              onClick={() => {
                const labelUrl = order.items.find(item => item.shipping_label)?.shipping_label;
                if (labelUrl) {
                  window.open(labelUrl, '_blank', 'noopener,noreferrer');
                } else {
                  setError('No shipping label available');
                  showToast('No shipping label available', 'error');
                  setTimeout(() => setError(null), 3000);
                }
              }}
              disabled={!order.items.some(item => item.shipping_label)}
            >
              Print Label
            </button>
          </div>
          <div className="right-column">
            {shipmentNotes.length > 0 ? (
              <ul className="notes-list">
                {shipmentNotes.map((note) => (
                  <li key={note.id} className="note-item">
                    <span className="note-tag admin-note">
                      {note.note}
                      <div className="note-meta">
                        {note.added_by}, {new Date(note.date_added).toLocaleString()}
                      </div>
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p>No shipment notes</p>
            )}
          </div>
        </div>
        <div className="supplier-dialog-actions" style={{ justifyContent: 'flex-end' }}>
          {hasScannedItems && (
            <button className="action-button" onClick={handleUnscanAll}>
              Unscan All
            </button>
          )}
          <button
            className="action-button save-button"
            onClick={handleSave}
            disabled={scannedItems.size === 0}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

export default OrderScannerDialog;